import React, { useState } from 'react';
import { Typography } from '../../../interfaces/typography';
import { prospectPartyCode } from '../../../cfg/endpoints';
import { MessageSquare } from 'lucide-react';
import { SessionDetailView, viewCanSeeComments } from '../../History/views';
import { SpeechTurn } from 'interfaces/sessions';
import { PartyRole } from 'interfaces/db';

export type TranscriptMessage = SpeechTurn & {
  hasComment?: boolean;
  commentCount?: number;
}

interface MessageTurnProps {
  message: TranscriptMessage;
  isSelected: boolean;
  onClick?: () => void;
  onCommentClick?: () => void;
  view: SessionDetailView;
}

// Comment icon component
const CommentIcon: React.FC<{ hasComment: boolean, commentCount?: number }> = ({ hasComment, commentCount = 0 }) => (
  <div className={`w-7 h-7 flex items-center justify-center rounded-full 
    ${hasComment 
      ? 'bg-indigo-100 text-indigo-600 border border-indigo-200' 
      : 'bg-white text-gray-400 hover:bg-gray-50 hover:text-indigo-500 border border-gray-200'
    }
    shadow-sm cursor-pointer hover:shadow relative`}
    title={hasComment ? `${commentCount} comment${commentCount !== 1 ? 's' : ''}` : "Add a comment"}
  >
    <MessageSquare size={16} />
    {hasComment && commentCount > 0 && (
      <div className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center">
        <span className="text-white text-[9px] font-bold">{commentCount > 9 ? '9+' : commentCount}</span>
      </div>
    )}
  </div>
);

const formatTimestamp = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(0);
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const MessageTurn: React.FC<MessageTurnProps> = ({ 
  message, 
  isSelected, 
  onClick,
  onCommentClick,
  view
}) => {
  const isProspect = message.role === PartyRole.PROSPECT;
  const [isHovered, setIsHovered] = useState(false);
  
  const canShowCommentInfo = viewCanSeeComments(view);
  
  return (
    <div 
      className="w-full flex flex-col group"
      style={{ alignItems: isProspect ? 'flex-start' : 'flex-end' }}
    >
      <div 
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="relative max-w-[85%]"
      >
        {/* Message bubble */}
        <div 
          className={`w-full p-2.5 rounded-lg relative cursor-pointer
            ${isProspect ? 'rounded-bl-none bg-[#f2f7fe]' : 'rounded-br-none bg-[#e9f5ec]'}
            ${isSelected ? '' : 'hover:shadow'}
            ${canShowCommentInfo && message.hasComment ? 'border-l-2 border-indigo-400' : ''}
            ${isHovered ? 'bg-opacity-95' : 'bg-opacity-100'}`}
        >
          <Typography variant="mediumParagraph">
            {message.text}
          </Typography>
          
          {/* Comment icon - positioned at top right for prospect messages, top left for rep messages */}
          {canShowCommentInfo && (
            <div 
              className={`absolute ${isProspect ? '-top-3.5 -right-2.5' : '-top-3.5 -left-2.5'} 
                ${message.hasComment ? 'opacity-100' : (isHovered ? 'opacity-100' : 'opacity-0')}
                transition-opacity duration-200 z-10`}
              onClick={(e) => {
                e.stopPropagation();
                if (onCommentClick) {
                  onCommentClick();
                }
              }}
            >
              <CommentIcon hasComment={!!message.hasComment} commentCount={message.commentCount} />
            </div>
          )}
        </div>
      </div>
      
      {/* Timestamp */}
      <Typography 
        variant="caption" 
        color="gray"
        className="px-1 opacity-50 group-hover:opacity-100 transition-opacity text-xs mt-1"
        style={{ alignSelf: isProspect ? 'flex-start' : 'flex-end' }}
      >
        {formatTimestamp(message.start)} - {formatTimestamp((message.end ?? message.start))}
      </Typography>
    </div>
  );
}; 